import { sha512Hex, accountedArchiveIdForConflict } from '@/services/sync/PendingOperationTerminalization';
type Row = Record<string, any>;
export type SupportConflictEffect = 'delete_already_satisfied' | 'unapplied_change' | 'needs_review';

/** Apresentação conservadora; nunca encerra conflito ou autoriza limpeza. */
export const createSupportConflictClassifier = (tables: Record<string, Row[]>) => {
  const byTable = Object.fromEntries(Object.entries(tables).map(([table, rows]) => [table, new Map(rows.map(row => [row.id, row]))]));
  const activeIds = new Set((tables.pending_operations ?? []).map(row => row.id));
  const activeTargets = new Set((tables.pending_operations ?? []).map(row => `${row.collection}:${row.item_id ?? row.itemId}`));
  const removed = new Set((tables.sync_tombstones ?? []).filter(row => row.applied_at).map(row => `${row.empresa_id}:${row.entity_type}:${row.entity_id}`));
  const find = (table: string, id: string, companyId: string) => {
    const row = byTable[table]?.get(id);
    return row?.empresa_id === companyId ? row : undefined;
  };
  return (conflict: Row): SupportConflictEffect => {
  try {
    if (!['open', 'blocked'].includes(String(conflict.status ?? '').toLowerCase())) return 'needs_review';
    const archive = byTable.accounted_pending_operations?.get(accountedArchiveIdForConflict(conflict.id));
    if (!archive) return 'needs_review';
    if (!conflict.pending_operation_id || !/^[a-f0-9]{128}$/.test(conflict.payload_sha512 ?? '') ||
        archive.empresa_id !== conflict.empresa_id || archive.user_id !== conflict.user_id ||
        archive.collection !== conflict.collection || archive.item_id !== conflict.item_id ||
        archive.reason !== conflict.reason || archive.accounting_state !== 'conflict_materialized' ||
        archive.payload_sha512 !== conflict.payload_sha512 || archive.data !== conflict.payload ||
        typeof conflict.payload !== 'string' && conflict.payload !== null ||
        sha512Hex(conflict.payload ?? '') !== conflict.payload_sha512 ||
        conflict.id !== `conflict:tombstone:${conflict.pending_operation_id}:${conflict.payload_sha512.slice(0, 24)}`) return 'needs_review';
    if (activeIds.has(conflict.pending_operation_id) || activeTargets.has(`${conflict.collection}:${conflict.item_id}`)) return 'needs_review';
    if (archive.operation !== 'delete' && archive.operation !== 'checklist_delete') {
      return ['create', 'update', 'checklist_partial_update'].includes(archive.operation) ? 'unapplied_change' : 'needs_review';
    }
    if (conflict.reason !== 'tombstoned_entity') return 'needs_review';
    const target = find(conflict.collection, conflict.item_id, conflict.empresa_id);
    const payload = conflict.payload ? JSON.parse(conflict.payload) : null;
    const ancestry = target ?? (payload && typeof payload === 'object' && !Array.isArray(payload) &&
      (!payload.empresa_id || payload.empresa_id === conflict.empresa_id) ? payload : {});
    const candidates = [{ collection: conflict.collection, id: conflict.item_id, row: target }];
    if (ancestry?.checklist_id) {
      const parent = find('guincho_checklists', ancestry.checklist_id, conflict.empresa_id);
      candidates.push({ collection: 'guincho_checklists', id: ancestry.checklist_id, row: parent });
    }
    if (ancestry?.template_id) {
      candidates.push({ collection: 'guincho_templates_checklist', id: ancestry.template_id,
        row: find('guincho_templates_checklist', ancestry.template_id, conflict.empresa_id) });
    }
    const satisfied = candidates.some(candidate => {
      if (candidate.row?.retired_at) return true;
      return !candidate.row && removed.has(`${conflict.empresa_id}:${candidate.collection}:${candidate.id}`);
    });
    return satisfied ? 'delete_already_satisfied' : 'needs_review';
  } catch { return 'needs_review'; }
  };
};
